/**
 * Module: PFStepStrip
 * Role: 6-stage Pantene progress strip with sub-progress, computed from checkpoint position.
 * Uses: tokens (P, S, T), flow-types (Checkpoint)
 * Used by: FlowScreen (mounted on all Pantene flow screens)
 * Glossary: ai/glossary/campaigns.md
 *
 * Design ref: ai/designs/miniapp/project/pantene-flow.jsx PFStepStrip (L150-177)
 * ARCH-1204 guardrail 5: step index is COMPUTED from checkpoint data, not hardcoded.
 */

import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint } from '../../../api/flow-types';

// ─── Visual stages ──────────────────────────────────────────────────────────
// 6 stages matching PF_STEPS from prototype (pantene-flow.jsx L16-23).
export const VISUAL_STAGES = [
  { code: 'BRIEF',    label: 'Бриф' },
  { code: 'DELIVERY', label: 'Доставка' },
  { code: 'VIDEO',    label: 'Видео' },
  { code: 'PUBLISH',  label: 'Соцсети' },
  { code: 'CONTROL',  label: 'Контроль' },
  { code: 'PAYOUT',   label: 'Выплата' },
] as const;

/**
 * Maps a checkpoint kind → visual stage index (1-based, matching VISUAL_STAGES).
 *
 * The mapping uses kind + slug heuristics. For kinds that appear in multiple
 * stages (PHOTO used for search/cart/order AND control_screenshot), we use
 * position context: PHOTO checkpoints after a PUBLISH checkpoint = CONTROL stage.
 */
function kindToStageIndex(kind: string, slug: string, hasSeenPublish: boolean): number {
  switch (kind) {
    case 'BRIEF':
      return 1;
    case 'WAIT':
      return 2;
    case 'UGC_TEXT':
    case 'UGC_PHOTO':
      return 3;
    case 'VIDEO_UPLOAD':
      // VIDEO_UPLOAD before any PUBLISH = Видео stage (before_after_video).
      // VIDEO_UPLOAD after a PUBLISH = Соцсети stage (social_video_upload).
      return hasSeenPublish ? 4 : 3;
    case 'MODERATION':
    case 'PUBLISH':
      return 4;
    case 'CART':
      return 1;
    case 'DONE':
      return 6;
    case 'PHOTO':
      // PHOTO kind is used for search/cart/order (stage 1) AND control_screenshot (stage 5).
      // Distinguish by whether we've seen a PUBLISH checkpoint before this position.
      if (hasSeenPublish || slug === 'control_screenshot') return 5;
      return 1; // search, cart, order
    default:
      return 1;
  }
}

/**
 * Compute the visual stage (1–6) for a given checkpoint position.
 *
 * Pure function — testable and deterministic.
 * EC-1: position=7 (before_after_video, VIDEO_UPLOAD) → visual step 3 (Видео).
 *
 * @param position - current checkpoint position (0-based)
 * @param checkpoints - all checkpoints sorted by position
 * @returns 1-based visual stage index
 */
export function positionToVisualStep(position: number, checkpoints: Checkpoint[]): number {
  const sorted = [...checkpoints].sort((a, b) => a.position - b.position);
  const target = sorted.find((c) => c.position === position);
  if (!target) return 1;

  // Track whether any PUBLISH checkpoint precedes the target.
  let hasSeenPublish = false;
  for (const cp of sorted) {
    if (cp.position >= target.position) break;
    if (cp.kind === 'PUBLISH') hasSeenPublish = true;
  }

  return kindToStageIndex(target.kind, target.slug, hasSeenPublish);
}

/**
 * Compute sub-progress within the current visual stage.
 *
 * Counts how many checkpoints in the same stage are before the current position
 * (done) vs total in that stage.
 *
 * @returns { current: done count (0-based), total: checkpoints in this stage }
 */
export function computeSubProgress(
  position: number,
  checkpoints: Checkpoint[],
): { current: number; total: number } {
  const sorted = [...checkpoints].sort((a, b) => a.position - b.position);
  const targetStage = positionToVisualStep(position, checkpoints);

  // Collect all checkpoints belonging to the same visual stage.
  let hasSeenPublish = false;
  const stageCheckpoints: Checkpoint[] = [];

  for (const cp of sorted) {
    const prevHasSeenPublish = hasSeenPublish;
    if (cp.kind === 'PUBLISH') hasSeenPublish = true;

    const cpStage = kindToStageIndex(cp.kind, cp.slug, prevHasSeenPublish);
    if (cpStage === targetStage) {
      stageCheckpoints.push(cp);
    }
  }

  const total = stageCheckpoints.length;
  const currentIdx = stageCheckpoints.findIndex((c) => c.position === position);
  const current = currentIdx >= 0 ? currentIdx : 0;

  return { current, total };
}

// ─── Track colour — matches prototype '#E8E3D4' ────────────────────────────
const TRACK_INACTIVE = '#E8E3D4';

interface PFStepStripProps {
  /** Current checkpoint position (0-based) */
  position: number;
  /** All checkpoints in the flow */
  checkpoints: Checkpoint[];
}

/**
 * PFStepStrip — 6-stage Pantene progress bar.
 *
 * Renders 6 segments (matching VISUAL_STAGES). Current stage is terracotta
 * with sub-progress fill; completed stages are green; future stages are inactive.
 *
 * Design ref: pantene-flow.jsx PFStepStrip
 */
export function PFStepStrip({ position, checkpoints }: PFStepStripProps) {
  const currentStage = positionToVisualStep(position, checkpoints);
  const sub = computeSubProgress(position, checkpoints);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
      <div style={{ display: 'flex', gap: 3 }}>
        {VISUAL_STAGES.map((s, i) => {
          const idx = i + 1;
          const done = idx < currentStage;
          const active = idx === currentStage;
          const bg = done ? P.success : active ? P.terracotta : TRACK_INACTIVE;

          // Active segment with sub-progress fill.
          if (active && sub.total > 1) {
            const fillPct = (sub.current / sub.total) * 100;
            return (
              <div
                key={s.code}
                data-testid={`pf-step-${i}`}
                style={{
                  flex: 1, height: 5, borderRadius: 3,
                  background: TRACK_INACTIVE,
                  position: 'relative', overflow: 'hidden',
                }}
              >
                <div
                  data-testid={`pf-step-${i}-fill`}
                  style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${fillPct}%`,
                    background: P.success,
                    transition: 'width 0.4s cubic-bezier(0.32, 0.72, 0, 1)',
                  }}
                />
              </div>
            );
          }

          return (
            <div
              key={s.code}
              data-testid={`pf-step-${i}`}
              style={{
                flex: 1, height: 5, borderRadius: 3,
                background: bg,
                transition: 'background 0.3s ease',
              }}
            />
          );
        })}
      </div>
      <div style={{
        font: T.caption, color: S.tertiaryLabel,
        letterSpacing: 0.2, textTransform: 'uppercase' as const,
        marginTop: 8,
      }}>
        {VISUAL_STAGES[currentStage - 1]?.label ?? ''} · {currentStage} из {VISUAL_STAGES.length}
      </div>
    </div>
  );
}
